import { momentStringFromDateTrunc, DateUnit, withinRange, dateAfter, dateBefore, getDateRange } from '../../dateUtils';
import { DateRange, DataRow, DataTable, ComparisonDelta, ValueAccessor, Value } from '../../models';
import { DefaultMeasureAccessor, DefaultTimestampAccessor } from '../../models/Accessor';
import {
  ComparisonSpecType,
  isNoComparison,
  isHistoricalComparisonSpec,
  isMarkBasedComparison,
} from '../../models/comparisonSpec/ComparisonSpecType';
import { isNullOrUndefined } from '../../models/DataTable';
import { HistoricalComparisonSpec } from '../../models/comparisonSpec/HistoricalComparisonSpec';

/**
 * @deprecated
 */
export const metricComparisonForPoint = (
  table: DataTable,
  currentValue: DataRow,
  comparison?: ComparisonSpecType
): ComparisonDelta | undefined => {
  const comparisonPoints = generateDataTableWithComparison(table, [currentValue], comparison);
  const point = comparisonPoints[comparisonPoints.length - 1];

  if (!point) {
    return undefined;
  }

  return computeMetricComparison(point);
};

/**
 * @deprecated use `TimeShift` or `RowShift` transforms via `materializeTable`
 */
export const generateDataTableWithComparison = (
  table: DataTable,
  windowedTable: DataTable,
  comparison?: ComparisonSpecType
): DataTable => {
  if (!comparison || isNoComparison(comparison)) {
    return windowedTable;
  }

  const comparisonPoints = generateComparisonPoints(comparison, table, windowedTable);

  return interleaveComparisonPoints(windowedTable, comparisonPoints, DefaultTimestampAccessor);
};

const generateComparisonPoints = (
  comparison: ComparisonSpecType,
  table: DataTable,
  windowedTable: DataTable
): DataTable => {
  const defaultOutputFunc = (row: DataRow) => {
    const output: DataRow = {};
    output.comparisonMeasure = row.measure;
    output.comparisonTimestamp = row.timestamp;
    return output;
  };
  if (isHistoricalComparisonSpec(comparison)) {
    const unit = momentStringFromDateTrunc(comparison.comparisonPeriod);
    if (!unit) {
      return generateMarkBasedComparison(table, windowedTable, 1, DefaultTimestampAccessor, defaultOutputFunc);
    }

    const currentRange = getDateRange(windowedTable);
    if (!currentRange) {
      return windowedTable;
    }

    return generateTimeBasedComparison(
      table,
      comparison,
      unit,
      currentRange,
      DefaultTimestampAccessor,
      defaultOutputFunc
    );
  } else if (isMarkBasedComparison(comparison)) {
    return generateMarkBasedComparison(table, windowedTable, 1, DefaultTimestampAccessor, defaultOutputFunc);
  } else {
    return windowedTable;
  }
};

export const generateMarkBasedComparison = (
  table: DataTable,
  windowedTable: DataTable,
  rowOffset: number,
  timestampAccessor: ValueAccessor<Date>,
  outputFunc: (input: DataRow) => DataRow
): DataTable => {
  if (windowedTable.length === 0) {
    return windowedTable;
  }
  const windowLastPoint = windowedTable[windowedTable.length - 1];
  const windowFirstPoint = windowedTable[0];

  // offset the window's points by 1 to get the window 1 mark prior
  let comparisonLastIndex = findIndexOfPoint(table, windowLastPoint) - rowOffset;
  let comparisonFirstIndex = findIndexOfPoint(table, windowFirstPoint) - rowOffset;

  return comparisonPointsForPriorMark(
    table,
    comparisonFirstIndex,
    comparisonLastIndex,
    rowOffset,
    timestampAccessor,
    outputFunc
  );
};

const comparisonPointsForPriorMark = (
  table: DataTable,
  comparisonFirstIndex: number,
  comparisonLastIndex: number,
  rowOffset: number,
  timestampAccessor: ValueAccessor<Date>,
  outputFunc: (input: DataRow) => DataRow
): DataRow[] => {
  let comparisonPoints: DataRow[] = [];
  for (let index = comparisonFirstIndex; index <= comparisonLastIndex; index++) {
    if (index > -1) {
      const timestamp = timestampAccessor(table[index + rowOffset]);
      const point = table[index];
      const o = outputFunc(point);
      comparisonPoints.push({ ...o, timestamp });
    }
  }
  return comparisonPoints;
};

const DEFAULT_NUMBER_OF_PERIODS = 1;

export const generateTimeBasedComparison = (
  table: DataTable,
  comparison: HistoricalComparisonSpec,
  unit: DateUnit,
  currentRange: DateRange,
  timestampAccessor: ValueAccessor<Date>,
  outputFunc: (input: DataRow) => DataRow
): DataTable => {
  const amount = comparison.numberOfPeriods || DEFAULT_NUMBER_OF_PERIODS;
  return comparisonPointsForRange(currentRange, amount, unit, table, timestampAccessor, outputFunc);
};

const findIndexOfPoint = (
  table: DataTable,
  point: DataRow,
  timestampAccessor: ValueAccessor<Date> = DefaultTimestampAccessor,
  measureAccessor: ValueAccessor<number | undefined> = DefaultMeasureAccessor
) => {
  return table.findIndex(
    (value) =>
      atSameTime(timestampAccessor(value), timestampAccessor(point)) &&
      measureAccessor(value) === measureAccessor(point)
  );
};

const comparisonPointsForRange = (
  currentRange: DateRange,
  amount: number,
  unit: DateUnit,
  table: DataTable,
  timestampAccessor: ValueAccessor<Date>,
  outputFunc: (input: DataRow) => DataRow
): DataTable => {
  const comparisonRange: DateRange = rangeBefore(currentRange, amount, unit);
  let comparisonPoints: DataRow[] = [];
  table.forEach((point) => {
    const date = timestampAccessor(point);
    if (withinRange(comparisonRange, date)) {
      const timestamp = dateAfter(date, amount, unit);
      const o = outputFunc(point);
      comparisonPoints.push({ ...o, timestamp });
    }
  });
  return comparisonPoints;
};

export const interleaveComparisonPoints = (
  windowedTable: DataTable,
  comparisonPoints: DataTable,
  timestampAccessor: ValueAccessor<Date>
): DataTable => {
  // The current index in the comparison array.
  // Since the data is sorted, we can walk the array down to keep up with 'data'.
  let comparisonIndex = 0;

  const dataTableWithComparison: DataRow[] = [];

  windowedTable.forEach((dp) => {
    while (
      comparisonIndex < comparisonPoints.length &&
      isBefore(timestampAccessor(comparisonPoints[comparisonIndex]), timestampAccessor(dp))
    ) {
      // Comparison Point with No Matching point in original table
      const comparisonPoint = comparisonPoints[comparisonIndex];
      comparisonIndex++;
      dataTableWithComparison.push({ ...comparisonPoint });
    }

    if (
      comparisonIndex < comparisonPoints.length &&
      atSameTime(timestampAccessor(dp), timestampAccessor(comparisonPoints[comparisonIndex]))
    ) {
      // comparison point with matching point in original table
      const comparisonPoint = comparisonPoints[comparisonIndex];
      comparisonIndex++;
      dataTableWithComparison.push({ ...comparisonPoint, ...dp });
    } else {
      // point in original table with no matching comparison point
      dataTableWithComparison.push(dp);
    }
  });
  return dataTableWithComparison;
};

const atSameTime = (comparisonDate: Date, originalDate: Date): boolean => {
  return comparisonDate.getTime() === originalDate.getTime();
};

const isBefore = (comparisonDate: Date, originalDate: Date): boolean => {
  return comparisonDate < originalDate;
};

export const rangeBefore = (currentRange: DateRange, amount: number, unit: DateUnit): DateRange => {
  const firstComparison = dateBefore(currentRange.first, amount, unit);
  const lastComparison = dateBefore(currentRange.last, amount, unit);
  return { first: firstComparison, last: lastComparison };
};

export const dropRowsWithUndefinedAccessorResult = (data: DataTable, accessor: ValueAccessor<Value>): DataTable => {
  return data.filter((d) => accessor(d) !== undefined);
};

export const renderablePoints = (input: DataTable): DataTable => input.filter((p) => p.measure !== undefined);

/// NOTE: This should just be removed in favor of using accessors in the client directly.
// To render points on a VictoryLine, we need meaure and timestamp to represent y and x,
// respectively. This returns the comparison point's measure and the translated timestamp
// to align the comparison measure on the x axis within the current line's domain
export const renderableComparisonPoints = (input: DataTable): DataTable =>
  input
    .filter((p) => p.comparisonMeasure !== undefined)
    .map((p) => ({ measure: p.comparisonMeasure, timestamp: p.timestamp }));

export const computeMetricComparison = (
  point: DataRow,
  measureAccessor: ValueAccessor<number | undefined> = DefaultMeasureAccessor,
  comparisonAccessor: ValueAccessor<number | undefined> = (row) => row.comparisonMeasure as number
): ComparisonDelta | undefined => {
  const measure = measureAccessor(point);
  const comp = comparisonAccessor(point);
  if (isNullOrUndefined(measure) || isNullOrUndefined(comp)) {
    return undefined;
  }
  const absolute = measure - comp;
  const percentage = absolute === 0 ? 0 : absolute / comp;

  return {
    absolute,
    percentage,
    comparedPoint: point,
  };
};
